import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Layers, MapPin, Pentagon, Route, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useInfraWithinParcels, useInfrastructureTaxonomy, useMapParcelsByIds } from '../api';
import { useMapStore } from '../MapContext';
import type { ArmedInfrastructureTemplate, FeatureType, InfrastructureTaxonomy, MapInfrastructure, TaxonomyTemplate, TemplateAttributeSchemaEntry } from '../types';
import { buildInfraTemplateId } from '../types';
import { useFarmInfrastructureScan } from '../useFarmInfrastructureScan';

function geometryIcon(geometryType?: FeatureType | null) {
  if (geometryType === 'Polygon') return Pentagon;
  if (geometryType === 'LineString') return Route;
  return MapPin;
}

function geometryDrawMode(geometryType?: FeatureType | null) {
  if (geometryType === 'Polygon') return 'polygon' as const;
  if (geometryType === 'LineString') return 'line' as const;
  return 'point' as const;
}

function normalizeAttributeSchema(template?: TaxonomyTemplate | null): TemplateAttributeSchemaEntry[] {
  const entries = Object.entries(template?.attributes || {});
  return entries.map(([key, definition]) => ({
    key,
    type: definition?.type || 'string',
    unit: definition?.unit,
    enum: definition?.enum,
    auto_fill_source: definition?.auto_fill_source || null,
  }));
}

function buildArmedTemplate(
  templateKey: string,
  checklistTemplate: InfrastructureTaxonomy['checklist']['sections'][number]['templates'][number],
  template?: TaxonomyTemplate | null,
): ArmedInfrastructureTemplate | null {
  if (!template) return null;
  const category = (template.category || checklistTemplate.infrastructure_category || 'buildings') as ArmedInfrastructureTemplate['category'];
  const subcategory = template.subcategory || checklistTemplate.subcategory || 'general';
  const type = checklistTemplate.key || templateKey;
  return {
    template_id: checklistTemplate.template_id || template.template_id || buildInfraTemplateId(category, subcategory, type),
    name: checklistTemplate.label || templateKey,
    category,
    subcategory,
    type,
    geometry_type: template.geometry_type || checklistTemplate.geometry_type || 'Point',
    industry_tags: template.industry_tags || [],
    attribute_schema: normalizeAttributeSchema(template),
    layer_key: template.layer_key || checklistTemplate.layer_key || null,
    checklist: template.checklist || null,
    land_categories: (template.land_categories || checklistTemplate.land_categories || []) as string[],
  };
}

function matchesChecklistTemplate(
  infrastructure: MapInfrastructure,
  sectionKey: string,
  templateKey: string,
  templateId?: string | null,
): boolean {
  const normalizedTemplateKey = String(templateKey || '').trim();
  const normalizedTemplateId = String(templateId || '').trim();
  const presetKey = String(infrastructure.attributes?.mapper_object_preset || '').trim();

  return (
    (sectionKey && String(infrastructure.layer_key || '').trim() === sectionKey)
    || (normalizedTemplateId && String(infrastructure.template_id || '').trim() === normalizedTemplateId)
    || (normalizedTemplateKey && String(infrastructure.infra_type || '').trim() === normalizedTemplateKey)
    || (normalizedTemplateKey && presetKey === normalizedTemplateKey)
  );
}

interface FarmChecklistPanelProps {
  onSelectTemplate?: (template: ArmedInfrastructureTemplate) => void;
}

export default function FarmChecklistPanel({ onSelectTemplate }: FarmChecklistPanelProps) {
  const {
    activeFarmGroup,
    activeParcel,
    setActiveCategory,
    setArmedTemplate,
    setDrawMode,
    setActiveParcel,
    setParcelInspectorOpen,
  } = useMapStore();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const scanFarm = useFarmInfrastructureScan();

  const groupParcels = useMapParcelsByIds(activeFarmGroup?.parcel_ids || [], {
    enabled: Boolean(activeFarmGroup?.parcel_ids?.length),
  });
  const farmInfrastructure = useInfraWithinParcels(activeFarmGroup?.parcel_ids || [], {
    enabled: Boolean(activeFarmGroup?.parcel_ids?.length),
  });

  const parcels = groupParcels.data ?? [];
  const primaryParcel = useMemo(() => {
    if (!activeFarmGroup) return activeParcel || null;
    if (activeParcel && activeFarmGroup.parcel_ids.includes(activeParcel.id)) return activeParcel;
    return parcels[0] || null;
  }, [activeFarmGroup, activeParcel, parcels]);
  const landCategory = primaryParcel?.land_category || null;
  const taxonomy = useInfrastructureTaxonomy(landCategory, {
    enabled: Boolean(activeFarmGroup),
  });

  const sections = useMemo(() => {
    const checklistSections = taxonomy.data?.checklist?.sections ?? [];
    const infrastructure = farmInfrastructure.data ?? [];
    const templates = taxonomy.data?.templates || {};

    return checklistSections.map((section) => {
      const items = section.templates.map((templateEntry) => {
        const template = templates[templateEntry.key] || null;
        const armedTemplate = buildArmedTemplate(templateEntry.key, templateEntry, template);
        const count = infrastructure.filter((item) => matchesChecklistTemplate(
          item,
          section.key,
          templateEntry.key,
          templateEntry.template_id,
        )).length;
        return {
          ...templateEntry,
          armedTemplate,
          attributeCount: armedTemplate?.attribute_schema.length || 0,
          mappedCount: count,
        };
      });

      return {
        ...section,
        mappedCount: items.reduce((sum, item) => sum + item.mappedCount, 0),
        items,
      };
    });
  }, [farmInfrastructure.data, taxonomy.data?.checklist?.sections, taxonomy.data?.templates]);
  const declaredTypes = useMemo(
    () => sections.flatMap((section) => section.items.map((item) => item.key)).slice(0, 40),
    [sections]
  );

  const handleSelectTemplate = (template: ArmedInfrastructureTemplate) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
      return;
    }
    setActiveCategory(template.category);
    setArmedTemplate(template);
    setDrawMode(geometryDrawMode(template.geometry_type));
    toast.info(`Draw the ${template.name} on the map, then double-click to finish.`, { duration: 3200 });
  };

  const handleScanFarm = async () => {
    if (!parcels.length) {
      toast.info('Add at least one parcel to this farm before running a scan.');
      return;
    }
    try {
      const result = await scanFarm.runScan({
        parcels,
        entityId: activeFarmGroup?.linked_entity_id || activeParcel?.linked_entity_id || undefined,
        landCategory: primaryParcel?.land_category || null,
        declaredTypes,
        replaceExisting: true,
      });
      if (result.targets[0]) {
        setActiveParcel(result.targets[0]);
        setParcelInspectorOpen(true);
      }
      toast.success('Farm scan started', {
        description: result.targets.length > 1
          ? `Started review scans for ${result.targets.length} farm parcels.`
          : 'Started review scan for this farm parcel.',
      });
    } catch (error) {
      toast.error('Farm scan failed', { description: String(error) });
    }
  };

  if (!activeFarmGroup) return null;

  return (
    <div className="mx-2.5 mt-2.5 rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white">
      <div className="border-b border-sky-100 px-3 py-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-sky-700" />
          <span className="text-sm font-semibold text-gray-900">Farm Checklist</span>
          {landCategory && (
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
              {landCategory}
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-gray-500">
          Use the contextualizer checklist to see which infrastructure layers this farm should have.
        </p>
        <button
          type="button"
          onClick={handleScanFarm}
          disabled={scanFarm.isPending || parcels.length === 0}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-40"
        >
          <Search className="h-3.5 w-3.5" />
          {scanFarm.isPending ? 'Scanning farm...' : 'Auto-detect farm'}
        </button>
      </div>

      <div className="space-y-2 px-2.5 py-2.5">
        {!primaryParcel ? (
          <div className="rounded-lg bg-white/80 px-3 py-3 text-xs text-gray-500">
            Add at least one parcel to this farm before mapping checklist layers.
          </div>
        ) : taxonomy.isLoading || groupParcels.isLoading ? (
          <div className="rounded-lg bg-white/80 px-3 py-3 text-xs text-gray-500">
            Loading checklist layers for this farm...
          </div>
        ) : sections.length === 0 ? (
          <div className="rounded-lg bg-white/80 px-3 py-3 text-xs text-gray-500">
            No checklist sections are available yet for this land category.
          </div>
        ) : (
          sections.map((section) => {
            const isOpen = openSections[section.key] ?? true;
            return (
              <div key={section.key} className="rounded-xl border border-sky-100 bg-white/90">
                <button
                  type="button"
                  onClick={() => setOpenSections((current) => ({ ...current, [section.key]: !isOpen }))}
                  className="flex w-full items-center gap-2 px-3 py-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs font-semibold text-gray-800">{section.label}</span>
                      {section.recommended && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          <Sparkles className="h-3 w-3" />
                          Recommended
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                      <span>{section.items.length} template{section.items.length === 1 ? '' : 's'}</span>
                      <span>{section.mappedCount} mapped</span>
                    </div>
                  </div>
                  <motion.div animate={{ rotate: isOpen ? 0 : -90 }} transition={{ duration: 0.2 }}>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-1.5 border-t border-sky-100 px-3 py-3">
                        {section.items.map((item) => {
                          const Icon = geometryIcon(item.geometry_type);
                          return (
                            <button
                              key={`${section.key}:${item.key}`}
                              type="button"
                              onClick={() => item.armedTemplate && handleSelectTemplate(item.armedTemplate)}
                              disabled={!item.armedTemplate}
                              className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                                item.mappedCount > 0
                                  ? 'border-emerald-200 bg-emerald-50/60'
                                  : 'border-gray-200 bg-white hover:border-sky-200 hover:bg-sky-50'
                              } disabled:cursor-default disabled:hover:border-gray-200 disabled:hover:bg-white`}
                            >
                              <span className="rounded-md bg-sky-100 p-1.5 text-sky-700">
                                <Icon className="h-3.5 w-3.5" />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-xs font-medium text-gray-800">{item.label}</div>
                                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-gray-500">
                                  <span>{item.geometry_type || 'Point'}</span>
                                  <span>{item.attributeCount} attr</span>
                                  {item.mappedCount > 0 && <span>{item.mappedCount} mapped</span>}
                                </div>
                              </div>
                              {item.armedTemplate && (
                                <span className="rounded-full bg-sky-100 px-2 py-1 text-[10px] font-semibold text-sky-700">
                                  Draw
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
